import { FC, useState } from 'react';
import { useDelete, useNavigation, useShowReturnType, useTranslate } from '@refinedev/core';
import {
  ActionButton,
  DetailRenderer,
  Loading,
  ShowPageWrapper,
  SomethingWentWrong,
  TriggerConfirmModal,
} from '~/components';
import { Card, ImageViewer, Tag, Checkbox } from '@scalingworks/react-admin-ui';
import { Challenge, ChallengeTypeEnum } from '~/api';
import { resourceNames } from '~/resources/resource-names';
import { formatDateRange } from '~/resources/helpers';
import { sentenceCase } from '@scalingworks/refine-react-admin';
import { Action } from '~/components/ActionButton/props';
import { getChallengeStatus } from '../helpers';
import { FiExternalLink } from 'react-icons/fi';

interface ChallengeShowPageProps {
  queryResult: useShowReturnType<Challenge>['queryResult'];
}

export const ChallengeShowPage: FC<ChallengeShowPageProps> = ({ queryResult }) => {
  const { data, isLoading } = queryResult;
  const challenge = data?.data;

  // ======================= HOOKS
  const t = useTranslate();
  const navigateTo = useNavigation();

  // ======================= API
  const { mutate: deleteChallenge, isLoading: deleting } = useDelete();

  // ======================= STATES
  const [showDelete, setShowDelete] = useState(false);

  // Check if we have a valid challenge
  if (isLoading) return <Loading />;
  if (!challenge) return <SomethingWentWrong />;

  // ======================= VARIABLES
  const {
    id,
    title,
    description,
    startDate,
    endDate,
    enabled,
    type,
    limitPerCustomer,
    asset,
    links,
    outlet,
    submissions,
  } = challenge;

  // Define actions
  const actions: Action[] = [
    {
      label: t('actions.edit', { fallback: 'Edit', ns: 'common' }),
      name: 'edit',
      onAction: () => navigateTo.edit(resourceNames.challenge, id),
    },
    {
      label: t('actions.delete', { fallback: 'Delete', ns: 'common' }),
      name: 'delete',
      onAction: () =>
        deleteChallenge(
          { resource: resourceNames.challenge, id },
          { onSuccess: () => navigateTo.list(resourceNames.challenge) }
        ),
      render: (onAction) => {
        return (
          <button type="button">
            <TriggerConfirmModal
              title={t('challenges.delete', { fallback: `Delete ${title}`, ns: 'common' })}
              visible={showDelete}
              onOpenChange={setShowDelete}
              onPressConfirm={onAction}
              description={
                <span>
                  {t('warnings.deleteConfirmation', {
                    fallback: 'Are you sure you want to delete',
                    ns: 'common',
                  })}
                  <span className="font-semibold text-error-300">{` ${title}`}</span>
                  {'? '}
                  {t('warnings.cannotUndo', { fallback: 'This action cannot be undone', ns: 'common' })}
                </span>
              }
            />
          </button>
        );
      },
    },
  ];

  // Determine status for display
  const { label: statusLabel, color: statusColor } = getChallengeStatus(startDate, endDate);

  return (
    <ShowPageWrapper
      resourceName={resourceNames.challenge}
      loading={isLoading}
      title={title}
    >
      <section className="flex flex-col space-y-4">
        <Card className="z-0">
          <Card.Header className="font-bold" bordered>
            <section className="flex flex-row items-center justify-between">
              <h1>{t('common.general', { fallback: 'General', ns: 'common' })}</h1>
              <ActionButton
                customTitle={t('common.action', { fallback: 'ACTION', ns: 'common' }).toUpperCase()}
                actions={actions}
              />
            </section>
          </Card.Header>
          <Card.Body>
            <DetailRenderer
              loading={isLoading}
              resource={resourceNames.challenge}
              data={{
                image: asset?.source,
                title,
                description,
                enabled,
                period: { startDate, endDate },
                status: statusLabel,
                type,
                limitPerCustomer: limitPerCustomer || '-',
                ...(outlet ? { outlet: outlet.name } : {}),
                submissions: submissions?.length || '0',
              }}
              render={{
                image: (src) => (
                  src ? <ImageViewer className="!w-32 !h-20 object-cover rounded-lg" src={src} /> : '-'
                ),
                status: (status) => (
                  <Tag color={statusColor}>{status}</Tag>
                ),
                period: ({ startDate, endDate }) => formatDateRange(startDate, endDate),
                type: (type) => sentenceCase(type),
                enabled: (enabled) => <Checkbox checked={enabled} disabled />,
              }}
            />
          </Card.Body>
        </Card>

        {links && links.length > 0 && (
          <Card>
            <Card.Header className="font-bold" bordered>
              <h1>{t('challenges.links', { fallback: 'Challenge Links', ns: 'common' })}</h1>
            </Card.Header>
            <Card.Body>
              <div className="space-y-4">
                {links.map((link, index) => (
                  <div key={link.id || index} className="border border-gray-200 rounded-md p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="font-medium text-gray-700">{t('challenges.form.platform', { fallback: 'Platform' })}</div>
                        <div>{sentenceCase(link.platform?.platformType || '')}</div>
                      </div>
                      <div>
                        <div className="font-medium text-gray-700">{t('challenges.form.url', { fallback: 'URL' })}</div>
                        <div className="flex items-center">
                          <a 
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-blue-600 hover:underline flex items-center"
                          >
                            {link.url}
                            <FiExternalLink className="ml-1 h-3 w-3" />
                          </a>
                        </div>
                      </div>
                      {link.title && (
                        <div>
                          <div className="font-medium text-gray-700">{t('challenges.form.linkTitle', { fallback: 'Title' })}</div>
                          <div>{link.title}</div>
                        </div>
                      )}
                      {link.description && (
                        <div className="md:col-span-2">
                          <div className="font-medium text-gray-700">{t('challenges.form.linkDescription', { fallback: 'Description' })}</div>
                          <div className="whitespace-pre-wrap">{link.description}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}

        {submissions && submissions.length > 0 && (
          <Card>
            <Card.Header className="font-bold" bordered>
              <h1>{t('challenges.submissions.title', { fallback: 'Submissions', ns: 'common' })}</h1>
            </Card.Header>
            <Card.Body>
              {/* Submissions could be implemented here */}
              <p>{t('challenges.submissions.count', { count: submissions.length, fallback: `${submissions.length} submissions`, ns: 'common' })}</p>
            </Card.Body>
          </Card>
        )}
      </section>
    </ShowPageWrapper>
  );
}; 